import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TranslatableInput.css';

/**
 * TranslatableItemFields - Combined name + description with unified language selector
 *
 * @param {object} item - Item object with name, description, translations
 * @param {function} onChange - Callback when values change
 * @param {boolean} nameRequired - Whether name is required
 */
const LANGS = ['es', 'en', 'pt'];

function TranslatableItemFields({ item, onChange, nameRequired = true, nameError }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('es');

  const handleTabKeyDown = (e) => {
    const idx = LANGS.indexOf(activeTab);
    let nextIdx = -1;
    if (e.key === 'ArrowRight') {
      nextIdx = (idx + 1) % LANGS.length;
    } else if (e.key === 'ArrowLeft') {
      nextIdx = (idx - 1 + LANGS.length) % LANGS.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = LANGS.length - 1;
    }
    if (nextIdx >= 0) {
      e.preventDefault();
      setActiveTab(LANGS[nextIdx]);
      e.currentTarget.parentElement.querySelectorAll('[role="tab"]')[nextIdx]?.focus();
    }
  };

  const hasContent = (lang) => {
    if (lang === 'es') return !!(item.name?.trim() || item.description?.trim());
    return !!(
      item.translations?.[lang]?.name?.trim() || item.translations?.[lang]?.description?.trim()
    );
  };

  const handleNameChange = (lang, value) => {
    if (lang === 'es') {
      onChange({ ...item, name: value });
    } else {
      const updated = { ...item.translations };
      if (!updated[lang]) updated[lang] = {};
      updated[lang].name = value;
      onChange({ ...item, translations: updated });
    }
  };

  const handleDescriptionChange = (lang, value) => {
    if (lang === 'es') {
      onChange({ ...item, description: value });
    } else {
      const updated = { ...item.translations };
      if (!updated[lang]) updated[lang] = {};
      updated[lang].description = value;
      onChange({ ...item, translations: updated });
    }
  };

  const getCurrentName = (lang) => {
    if (lang === 'es') return item.name || '';
    return item.translations?.[lang]?.name || '';
  };

  const getCurrentDescription = (lang) => {
    if (lang === 'es') return item.description || '';
    return item.translations?.[lang]?.description || '';
  };

  return (
    <div className="translatable-item-fields">
      {/* Unified Language Selector */}
      <div className="language-tabs" role="tablist" aria-label={t('menu.languageTabs')}>
        {LANGS.map((lang) => (
          <button
            key={lang}
            type="button"
            role="tab"
            id={`item-fields-tab-${lang}`}
            aria-selected={activeTab === lang}
            aria-controls={`item-fields-panel-${lang}`}
            tabIndex={activeTab === lang ? 0 : -1}
            className={`tab ${activeTab === lang ? 'active' : ''}`}
            onClick={() => setActiveTab(lang)}
            onKeyDown={handleTabKeyDown}
          >
            {lang === 'es' ? '🇪🇸 ES' : lang === 'en' ? '🇬🇧 EN' : '🇧🇷 PT'}
            <span
              className={`tab-dot ${hasContent(lang) ? 'tab-dot--filled' : 'tab-dot--empty'}`}
            />
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`item-fields-panel-${activeTab}`}
        aria-labelledby={`item-fields-tab-${activeTab}`}
      >
        {/* Name Field */}
        <div className={`translatable-input${nameError && activeTab === 'es' ? ' has-error' : ''}`}>
          <label htmlFor={`item-name-${activeTab}`}>
            Nombre {nameRequired && activeTab === 'es' && <span className="required">*</span>}
          </label>
          <input
            id={`item-name-${activeTab}`}
            type="text"
            value={getCurrentName(activeTab)}
            onChange={(e) => handleNameChange(activeTab, e.target.value)}
            aria-required={nameRequired && activeTab === 'es'}
            placeholder={
              activeTab === 'es'
                ? ''
                : t('common.translationPlaceholder', {
                    lng: activeTab,
                    field: t('menu.name', { lng: activeTab }),
                    lang: activeTab.toUpperCase(),
                  })
            }
            className={nameError && activeTab === 'es' ? 'input-error' : ''}
          />
          {nameError && activeTab === 'es' && (
            <span className="field-error" role="alert">
              {nameError}
            </span>
          )}
        </div>

        {/* Description Field */}
        <div className="translatable-input">
          <label htmlFor={`item-desc-${activeTab}`}>
            Descripción <span className="optional-hint">({t('common.optional')})</span>
          </label>
          <textarea
            id={`item-desc-${activeTab}`}
            value={getCurrentDescription(activeTab)}
            onChange={(e) => handleDescriptionChange(activeTab, e.target.value)}
            placeholder={
              activeTab === 'es'
                ? ''
                : t('common.translationPlaceholder', {
                    lng: activeTab,
                    field: t('menu.description', { lng: activeTab }),
                    lang: activeTab.toUpperCase(),
                  })
            }
          />
        </div>
      </div>

      {activeTab !== 'es' && <small className="help-text">{t('translationHelperText')}</small>}
    </div>
  );
}

export default TranslatableItemFields;
